/**
 * Dedicated Calendar (provider-level self-hosted page) models.
 */

import type { PageSection } from './page-section.models';

/**
 * Legal page type options.
 */
export type LegalPageType = 'None' | 'Internal' | 'External';

/**
 * Dedicated Calendar DTO.
 */
export interface DedicatedCalendar {
  id: string;
  providerId: string;
  cloudflareSubdomain: string;
  fullUrl: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  textMutedColor: string;
  borderRadius: DedicatedCalendarBorderRadius;
  fontFamily: DedicatedCalendarFontFamily;
  themeMode: DedicatedCalendarThemeMode;
  backgroundPattern: DedicatedCalendarBackgroundPattern;
  // Extended theme properties
  buttonRadius: DedicatedCalendarBorderRadius;
  shadowIntensity: DedicatedCalendarShadowIntensity;
  containerWidth: DedicatedCalendarContainerWidth;
  sectionSpacing: DedicatedCalendarSectionSpacing;
  linkColor: string;
  linkHoverColor: string;
  buttonTextColor: string;
  borderColor: string;
  // Legal pages
  imprintType: LegalPageType;
  imprintExternalUrl: string | null;
  imprintContentDe: string | null;
  imprintContentEn: string | null;
  privacyType: LegalPageType;
  privacyExternalUrl: string | null;
  privacyContentDe: string | null;
  privacyContentEn: string | null;
  // Offer page configuration
  offerPageSettings: string | null;
  offerPageContentDe: string | null;
  offerPageContentEn: string | null;
  // Publish status
  isPublished: boolean;
  firstPublishedAtUtc: string | null;
  lastPublishedAtUtc: string | null;
  hasUnpublishedChanges: boolean;
  // Audit fields
  createdAtUtc: string;
  updatedAtUtc: string;
  // Page sections (optional, included when fetching)
  sections?: PageSection[];
}

/**
 * DTO for creating a dedicated calendar.
 */
export interface CreateDedicatedCalendarDto {
  subdomain: string;
}

/**
 * DTO for updating dedicated calendar theme.
 */
export interface UpdateDedicatedCalendarThemeDto {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  textMutedColor: string;
  borderRadius: DedicatedCalendarBorderRadius;
  fontFamily: DedicatedCalendarFontFamily;
  themeMode: DedicatedCalendarThemeMode;
  backgroundPattern: DedicatedCalendarBackgroundPattern;
  // Extended theme properties
  buttonRadius: DedicatedCalendarBorderRadius;
  shadowIntensity: DedicatedCalendarShadowIntensity;
  containerWidth: DedicatedCalendarContainerWidth;
  sectionSpacing: DedicatedCalendarSectionSpacing;
  linkColor: string;
  linkHoverColor: string;
  buttonTextColor: string;
  borderColor: string;
  // Legal pages
  imprintType: LegalPageType;
  imprintExternalUrl: string | null;
  imprintContentDe: string | null;
  imprintContentEn: string | null;
  privacyType: LegalPageType;
  privacyExternalUrl: string | null;
  privacyContentDe: string | null;
  privacyContentEn: string | null;
}

/**
 * Request for logo upload URL (dedicated calendar).
 */
export interface DedicatedCalendarLogoUploadRequest {
  contentType: string;
  fileSize: number;
}

/**
 * Response for logo upload URL.
 */
export interface LogoUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  expiresInSeconds: number;
}

/**
 * DTO for updating offer page configuration.
 */
export interface UpdateOfferPageConfigDto {
  offerPageSettings: string | null;
  offerPageContentDe: string | null;
  offerPageContentEn: string | null;
}

/**
 * Offer page form field configuration.
 */
export interface OfferPageFormField {
  visible: boolean;
  required: boolean;
}

/**
 * Offer page settings (parsed from JSON).
 */
export interface OfferPageSettings {
  layoutVariant: 'default' | 'compact' | 'modern';
  showTimeline: boolean;
  showProviderMessage: boolean;
  showExpiryBadge: boolean;
  formFields: Record<string, OfferPageFormField>;
}

/**
 * Offer page content per language (parsed from JSON).
 */
export interface OfferPageContent {
  pageTitle: string;
  pageSubtitle: string;
  acceptButtonText: string;
  declineButtonText: string;
  successTitle: string;
  successMessage: string;
  declinedTitle: string;
  declinedMessage: string;
  timelineLabels: {
    requested: string;
    offerSent: string;
    pending: string;
  };
}

/**
 * Default offer page settings.
 */
export const DEFAULT_OFFER_PAGE_SETTINGS: OfferPageSettings = {
  layoutVariant: 'default',
  showTimeline: true,
  showProviderMessage: true,
  showExpiryBadge: true,
  formFields: {
    customerName: { visible: true, required: true },
    street: { visible: true, required: true },
    houseNumber: { visible: true, required: true },
    postalCode: { visible: true, required: true },
    city: { visible: true, required: true },
    phoneNumber: { visible: true, required: false },
    acceptsTerms: { visible: true, required: true },
    acceptsProviderPrivacy: { visible: true, required: false },
  },
};

/**
 * Default offer page content (German).
 */
export const DEFAULT_OFFER_PAGE_CONTENT_DE: OfferPageContent = {
  pageTitle: 'Ihr Terminangebot',
  pageSubtitle: 'von {{companyName}}',
  acceptButtonText: 'Angebot annehmen',
  declineButtonText: 'Angebot ablehnen',
  successTitle: 'Vielen Dank!',
  successMessage: 'Ihr Termin wurde bestätigt. Sie erhalten in Kürze eine Bestätigung per E-Mail.',
  declinedTitle: 'Angebot abgelehnt',
  declinedMessage: 'Das Angebot wurde abgelehnt.',
  timelineLabels: {
    requested: 'Angefragt',
    offerSent: 'Angebot gesendet',
    pending: 'Ihre Antwort',
  },
};

/**
 * Default offer page content (English).
 */
export const DEFAULT_OFFER_PAGE_CONTENT_EN: OfferPageContent = {
  pageTitle: 'Your Appointment Offer',
  pageSubtitle: 'from {{companyName}}',
  acceptButtonText: 'Accept Offer',
  declineButtonText: 'Decline Offer',
  successTitle: 'Thank you!',
  successMessage: 'Your appointment has been confirmed. You will receive a confirmation email shortly.',
  declinedTitle: 'Offer Declined',
  declinedMessage: 'The offer has been declined.',
  timelineLabels: {
    requested: 'Requested',
    offerSent: 'Offer Sent',
    pending: 'Your Response',
  },
};

/**
 * Response from adding a custom domain.
 */
export interface AddCustomDomainResponse {
  dedicatedCalendar: unknown;
  requiredCnameTarget: string;
  status: string;
}

/**
 * Response from checking custom domain status.
 */
export interface CustomDomainStatusResponse {
  customDomain: string;
  status: 'pending_verification' | 'pending_ssl' | 'active' | 'failed';
  sslStatus: string;
  cnameTarget: string;
  isActive: boolean;
}

/**
 * Border radius options for dedicated calendar.
 */
export type DedicatedCalendarBorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Font family options for dedicated calendar.
 */
export type DedicatedCalendarFontFamily = 'system' | 'serif' | 'sans-serif' | 'monospace' | 'inter' | 'roboto' | 'open-sans' | 'poppins' | 'playfair';

/**
 * Theme mode options for dedicated calendar.
 * - light: Always use light theme
 * - dark: Always use dark theme
 * - system: Follow visitor's system preference
 */
export type DedicatedCalendarThemeMode = 'light' | 'dark' | 'system';

/**
 * Background pattern options for dedicated calendar.
 */
export type DedicatedCalendarBackgroundPattern = 'none' | 'dots' | 'grid' | 'waves' | 'gradient' | 'mesh' | 'noise';

/**
 * Shadow intensity options for dedicated calendar.
 */
export type DedicatedCalendarShadowIntensity = 'none' | 'sm' | 'md' | 'lg';

/**
 * Container width options for dedicated calendar.
 */
export type DedicatedCalendarContainerWidth = 'narrow' | 'default' | 'wide' | 'full';

/**
 * Section spacing options for dedicated calendar.
 */
export type DedicatedCalendarSectionSpacing = 'compact' | 'default' | 'relaxed';

/**
 * Border radius display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_BORDER_RADIUS_OPTIONS: { value: DedicatedCalendarBorderRadius; i18nKey: string }[] = [
  { value: 'none', i18nKey: 'dedicatedCalendar.borderRadius.none' },
  { value: 'sm', i18nKey: 'dedicatedCalendar.borderRadius.sm' },
  { value: 'md', i18nKey: 'dedicatedCalendar.borderRadius.md' },
  { value: 'lg', i18nKey: 'dedicatedCalendar.borderRadius.lg' },
  { value: 'xl', i18nKey: 'dedicatedCalendar.borderRadius.xl' },
  { value: 'full', i18nKey: 'dedicatedCalendar.borderRadius.full' },
];

/**
 * Font family display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_FONT_FAMILY_OPTIONS: { value: DedicatedCalendarFontFamily; labelDe: string; labelEn: string }[] = [
  { value: 'system', labelDe: 'System (Standard)', labelEn: 'System (Default)' },
  { value: 'inter', labelDe: 'Inter', labelEn: 'Inter' },
  { value: 'roboto', labelDe: 'Roboto', labelEn: 'Roboto' },
  { value: 'open-sans', labelDe: 'Open Sans', labelEn: 'Open Sans' },
  { value: 'poppins', labelDe: 'Poppins', labelEn: 'Poppins' },
  { value: 'sans-serif', labelDe: 'Helvetica / Arial', labelEn: 'Helvetica / Arial' },
  { value: 'playfair', labelDe: 'Playfair Display (Serif)', labelEn: 'Playfair Display (Serif)' },
  { value: 'serif', labelDe: 'Georgia (Serif)', labelEn: 'Georgia (Serif)' },
  { value: 'monospace', labelDe: 'Monospace', labelEn: 'Monospace' },
];

/**
 * Theme mode display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_THEME_MODE_OPTIONS: { value: DedicatedCalendarThemeMode; i18nKey: string }[] = [
  { value: 'light', i18nKey: 'dedicatedCalendar.themeMode.light' },
  { value: 'dark', i18nKey: 'dedicatedCalendar.themeMode.dark' },
  { value: 'system', i18nKey: 'dedicatedCalendar.themeMode.system' },
];

/**
 * Background pattern display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_BACKGROUND_PATTERN_OPTIONS: { value: DedicatedCalendarBackgroundPattern; i18nKey: string }[] = [
  { value: 'none', i18nKey: 'dedicatedCalendar.backgroundPattern.none' },
  { value: 'dots', i18nKey: 'dedicatedCalendar.backgroundPattern.dots' },
  { value: 'grid', i18nKey: 'dedicatedCalendar.backgroundPattern.grid' },
  { value: 'waves', i18nKey: 'dedicatedCalendar.backgroundPattern.waves' },
  { value: 'gradient', i18nKey: 'dedicatedCalendar.backgroundPattern.gradient' },
  { value: 'mesh', i18nKey: 'dedicatedCalendar.backgroundPattern.mesh' },
  { value: 'noise', i18nKey: 'dedicatedCalendar.backgroundPattern.noise' },
];

/**
 * Default theme values.
 */
export const DEFAULT_DEDICATED_CALENDAR_THEME: UpdateDedicatedCalendarThemeDto = {
  primaryColor: '#3B82F6',
  secondaryColor: '#64748B',
  accentColor: '#F59E0B',
  backgroundColor: '#FFFFFF',
  surfaceColor: '#F8FAFC',
  textColor: '#0F172A',
  textMutedColor: '#64748B',
  borderRadius: 'md',
  fontFamily: 'system',
  themeMode: 'system',
  backgroundPattern: 'none',
  // Extended theme defaults
  buttonRadius: 'md',
  shadowIntensity: 'sm',
  containerWidth: 'default',
  sectionSpacing: 'default',
  linkColor: '#3B82F6',
  linkHoverColor: '#2563EB',
  buttonTextColor: '#FFFFFF',
  borderColor: '#E2E8F0',
  // Legal pages (default to None - requires configuration before publishing)
  imprintType: 'None',
  imprintExternalUrl: null,
  imprintContentDe: null,
  imprintContentEn: null,
  privacyType: 'None',
  privacyExternalUrl: null,
  privacyContentDe: null,
  privacyContentEn: null,
};

/**
 * Shadow intensity display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_SHADOW_INTENSITY_OPTIONS: { value: DedicatedCalendarShadowIntensity; i18nKey: string }[] = [
  { value: 'none', i18nKey: 'dedicatedCalendar.shadowIntensity.none' },
  { value: 'sm', i18nKey: 'dedicatedCalendar.shadowIntensity.sm' },
  { value: 'md', i18nKey: 'dedicatedCalendar.shadowIntensity.md' },
  { value: 'lg', i18nKey: 'dedicatedCalendar.shadowIntensity.lg' },
];

/**
 * Container width display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_CONTAINER_WIDTH_OPTIONS: { value: DedicatedCalendarContainerWidth; i18nKey: string }[] = [
  { value: 'narrow', i18nKey: 'dedicatedCalendar.containerWidth.narrow' },
  { value: 'default', i18nKey: 'dedicatedCalendar.containerWidth.default' },
  { value: 'wide', i18nKey: 'dedicatedCalendar.containerWidth.wide' },
  { value: 'full', i18nKey: 'dedicatedCalendar.containerWidth.full' },
];

/**
 * Section spacing display labels (i18n keys).
 */
export const DEDICATED_CALENDAR_SECTION_SPACING_OPTIONS: { value: DedicatedCalendarSectionSpacing; i18nKey: string }[] = [
  { value: 'compact', i18nKey: 'dedicatedCalendar.sectionSpacing.compact' },
  { value: 'default', i18nKey: 'dedicatedCalendar.sectionSpacing.default' },
  { value: 'relaxed', i18nKey: 'dedicatedCalendar.sectionSpacing.relaxed' },
];
